import React from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { type TemplateOption, nudgeNoteTypesFor, useTemplateOptions } from "../../hooks/useTemplateOptions";
import { getRecentNoteTypes } from "../../service/notes.service";
import { type SchedulePick, type ScheduleSlot, useGetTodaySchedulePickQuery } from "../../service/schedulePick.service";
import { cacheClients } from "../../slices/clientSlice";
import { EventType, trackEvent } from "../../utils/analytics";
import { eventEmitter } from "../../utils/eventEmitter";
import {
  type SessionTemplate,
  readStoredTemplateChoice,
  rememberTemplateChoice,
  resolveSessionTemplate,
} from "../../utils/sessionTemplate";
import { type QuickStartView, loadDismissed, quickStartView, saveDismissed, subscribeDismissed } from "./quickStart";

// The sheet itself only changes when a note lands (refetched on that event) or a pull runs.
const SCHEDULE_POLL_MS = 5 * 60 * 1000;
// Who is "around now" and inside the next two hours drifts with the clock; re-cut the list on a slow tick.
const CLOCK_TICK_MS = 60 * 1000;
// Coming back to the tab refetches the template list, at most this often.
const TEMPLATE_FOCUS_REFRESH_MS = 30 * 1000;

export type QuickStartSchedule = {
  /** The card asks for today's sheet (everywhere but the demo). */
  enabled: boolean;
  pick: SchedulePick | null;
  /** What the card offers; see ``quickStartView``. */
  view: QuickStartView;
  /** × : close the list (Start Encounter until the schedule moves on). */
  dismiss: () => void;
  /** Show the list again after ×. */
  restore: () => void;
};

/**
 * Today's sheet for the Start card. Reads the same ranked sheet as the Start Encounter
 * drawer (``GET /client/schedule/today/``), so the card and the drawer agree on who is
 * next; one subscription is shared by every mounted card.
 */
export const useQuickStartSchedule = (): QuickStartSchedule => {
  const dispatch = useAppDispatch();
  const { isDemoPath } = useAppSelector((state) => state.appState);
  // The card only mounts for accounts a pulled schedule reaches (``has_ehr_schedule``).
  const enabled = !isDemoPath;

  const { data, refetch } = useGetTodaySchedulePickQuery(
    {},
    { skip: !enabled, pollingInterval: SCHEDULE_POLL_MS, refetchOnFocus: true, refetchOnMountOrArgChange: true },
  );
  const pick = enabled ? data ?? null : null;

  // Read from storage through a subscription, so both mounted cards agree after × or Show.
  const day = pick?.day;
  const dismissedId = React.useSyncExternalStore(
    subscribeDismissed,
    React.useCallback(() => loadDismissed(day), [day]),
  );
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), CLOCK_TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Rows carry full client objects; caching them lets the recording header and the
  // client page resolve the patient without another fetch.
  React.useEffect(() => {
    if (!pick) return;
    const clients = [...pick.recommended, ...pick.up_next, ...pick.documented].map((slot) => slot.client);
    if (clients.length) dispatch(cacheClients(clients));
  }, [pick, dispatch]);

  // A note that starts or lands moves its patient to "documented": re-rank right away.
  React.useEffect(() => {
    if (!enabled) return;
    const onNotesChanged = () => {
      refetch();
    };
    eventEmitter.on("noteCreated", onNotesChanged);
    eventEmitter.on("refreshNotes", onNotesChanged);
    return () => {
      eventEmitter.off("noteCreated", onNotesChanged);
      eventEmitter.off("refreshNotes", onNotesChanged);
    };
  }, [enabled, refetch]);

  // Cut against the clock each time the sheet arrives and on every tick.
  // biome-ignore lint/correctness/useExhaustiveDependencies: tick re-runs the cut as time passes
  const view = React.useMemo(() => quickStartView(pick, { dismissedId }), [pick, dismissedId, tick]);

  const dismiss = React.useCallback(() => {
    if (view.kind !== "list" || !pick) return;
    const id = view.slots[view.first].client.id;
    if (id == null) return;
    trackEvent(EventType.QUICK_START_DISMISSED, {
      view: view.kind,
      candidates: view.slots.length,
      confidence: pick.suggestion?.confidence ?? null,
    });
    saveDismissed(pick.day, id);
  }, [pick, view]);

  const restore = React.useCallback(() => {
    if (pick) saveDismissed(pick.day, null);
    trackEvent(EventType.QUICK_START_RESTORED);
  }, [pick]);

  return { enabled, pick, view, dismiss, restore };
};

// Both mounted cards ask at once on load; share that one request.
let recentInFlight: Promise<number[]> | null = null;
const fetchRecentNoteTypes = (): Promise<number[]> => {
  if (!recentInFlight) {
    recentInFlight = getRecentNoteTypes()
      .then((response) => response.data.note_types ?? [])
      .finally(() => {
        recentInFlight = null;
      });
  }
  return recentInFlight;
};

export type SessionTemplateState = ReturnType<typeof useSessionTemplate>;

/**
 * The template a Quick Start will use, named before the clinician presses it.
 * See ``resolveSessionTemplate`` for the order of evidence. ``template`` is the card's default
 * (the footer picker, and every row without a visit-type suggestion); ``resolveFor(slot)`` is
 * what a Start on that row will actually use.
 */
export const useSessionTemplate = (pick?: SchedulePick | null) => {
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const { isCreateModalVisible, isAITemplateModalVisible } = useAppSelector((state) => state.appState);
  const noteTypes = React.useMemo(() => nudgeNoteTypesFor(userInfo), [userInfo]);

  // Templates change under a card that stays mounted all day: edited or deleted in My templates,
  // added from the drawer, unshared by an admin elsewhere. Refetch the list when either closes and
  // when the window regains focus, so a template that is gone stops being offered (and sent).
  const [templatesVersion, setTemplatesVersion] = React.useState(0);
  const editorsOpen = isCreateModalVisible || isAITemplateModalVisible;
  const wasEditorOpen = React.useRef(editorsOpen);
  React.useEffect(() => {
    if (wasEditorOpen.current && !editorsOpen) setTemplatesVersion((v) => v + 1);
    wasEditorOpen.current = editorsOpen;
  }, [editorsOpen]);
  React.useEffect(() => {
    let last = Date.now();
    let trailing: ReturnType<typeof setTimeout> | null = null;
    const refresh = () => {
      last = Date.now();
      setTemplatesVersion((v) => v + 1);
    };
    // Throttled, never dropped: a focus inside the window refreshes when the window ends.
    const onFocus = () => {
      const wait = TEMPLATE_FOCUS_REFRESH_MS - (Date.now() - last);
      if (wait <= 0) {
        // A background tab can hold a trailing timer past its deadline; this refresh replaces it.
        if (trailing) clearTimeout(trailing);
        trailing = null;
        refresh();
      } else if (!trailing) {
        trailing = setTimeout(() => {
          trailing = null;
          refresh();
        }, wait);
      }
    };
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      if (trailing) clearTimeout(trailing);
    };
  }, []);

  const { groups, options, customNotesLoaded, loadGallery } = useTemplateOptions({
    noteTypes,
    galleryOnMount: false,
    refreshKey: templatesVersion,
  });
  const galleryLoaded = useAppSelector((state) => state.appState.templates.length > 0);
  const [recent, setRecent] = React.useState<number[] | null>(null);
  const [stored, setStored] = React.useState<number | null>(() => readStoredTemplateChoice());
  const [chosen, setChosen] = React.useState<number | string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    fetchRecentNoteTypes()
      .then((noteTypes) => {
        if (!cancelled) setRecent(noteTypes);
      })
      .catch((error) => {
        console.error("Could not load recent templates:", error);
        if (!cancelled) setRecent([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The drawer may have just remembered a different template; read it again when it closes. A pick
  // on the card was for that start: once a drawer has been used, the card yields to what the
  // drawer remembered (a gallery pick there is never stored, so comparing values can't tell).
  const wasDrawerOpen = React.useRef(isCreateModalVisible);
  React.useEffect(() => {
    if (wasDrawerOpen.current && !isCreateModalVisible) setChosen(null);
    wasDrawerOpen.current = isCreateModalVisible;
    if (!isCreateModalVisible) setStored(readStoredTemplateChoice());
  }, [isCreateModalVisible]);

  const template = React.useMemo(
    () => resolveSessionTemplate({ options, chosen, stored, recent }),
    [options, chosen, stored, recent],
  );

  const minConfidence = pick?.template_min_confidence;
  const resolveFor = React.useCallback(
    (slot: ScheduleSlot): SessionTemplate | null =>
      resolveSessionTemplate({
        options,
        chosen,
        suggested: slot.template_suggestion,
        minConfidence,
        stored,
        recent,
      }),
    [options, chosen, stored, recent, minConfidence],
  );

  const choose = React.useCallback(
    (option: TemplateOption) => {
      rememberTemplateChoice(option.value, Boolean(option.is_template));
      setStored(readStoredTemplateChoice());
      setChosen(option.value);
      trackEvent(EventType.QUICK_START_TEMPLATE_CHANGED, {
        group: option.group,
        had_template: template !== null,
        from_source: template?.source ?? null,
      });
    },
    [template],
  );

  return {
    template,
    /** The template a Start on this row will use: the card's, unless the visit type suggests one. */
    resolveFor,
    /** True once the clinician picked on the card: their pick overrides every row's suggestion. */
    hasChosen: chosen !== null,
    /** False until both the picker's options and the recent history have loaded. */
    ready: customNotesLoaded && recent !== null,
    groups,
    options,
    choose,
    /** The gallery is only fetched once the picker is about to show it. */
    loadGallery: galleryLoaded ? () => {} : loadGallery,
  };
};
